import type {
  AnyEntity,
  Change,
  ContentConflict,
  EntityKind,
  Mutation,
  MutationEnvelope,
  PushConflict,
} from "@hq/contracts";
import { mutationTargetId, SYNC_PROTOCOL_VERSION } from "@hq/contracts";
import {
  applyMutation,
  isRevisionGuarded,
  MutationTargetMissing,
  type NowContext,
  nowContext,
} from "@hq/core";
import type { EntityRepository, LocalStore, PendingMutation } from "@hq/local-store";
import type { NetworkState } from "./network";
import { defaultTimers, type Timers } from "./timers";
import { recordOwnRevision } from "./draftRevision";
import type { SyncTransport } from "./transport";

export type SyncPhase = "idle" | "pushing" | "pulling" | "offline" | "error";

export type SyncStatus = {
  phase: SyncPhase;
  pendingCount: number;
  lastPushAt?: string;
  lastPullAt?: string;
  lastError?: string;
  lastErrorAt?: string;
  cursor: number;
  /** The server no longer speaks this protocol version; syncing is paused until the app updates. */
  updateRequired?: boolean;
};

/** Stored at `kv("conflict:<entity>:<id>")` when the server refused a revision-guarded write. */
export type ConflictRecord = {
  mutationId: string;
  serverRevision: number;
  conflictId?: string;
  at: string;
  /** The refused mutation, so the UI can offer "keep mine" without a round trip. */
  mutation: Mutation;
};

/** Stored at `kv("dead-letter:<mutationId>")` for a mutation the server keeps rejecting. */
export type DeadLetterRecord = {
  envelope: MutationEnvelope;
  code: string;
  message: string;
  attempts: number;
  at: string;
};

export type SyncEngineOptions = {
  store: LocalStore;
  /** Authentication gates all sync entry points while local services remain available. */
  enabled?: () => boolean;
  transport: SyncTransport;
  /** Shared with `createServices` so content writes are never coalesced into an in-flight mutation. */
  services?: { inFlight: Set<string> };
  network?: NetworkState;
  /** Periodic sync interval while online. */
  intervalMs?: number;
  /** Mutations per push. */
  batchSize?: number;
  /** Changes per pull page. */
  pullLimit?: number;
  /** Debounce for `kick()`. */
  kickDelayMs?: number;
  timers?: Timers;
  clock?: () => NowContext;
};

export interface SyncEngine {
  /** Sync now, then every `intervalMs` while online, and whenever connectivity returns. */
  start(): void;
  stop(): void;
  /** Schedule a sync soon (debounced). Services call this after every local write. */
  kick(): void;
  /** Push then pull, now. A call while one is running waits and coalesces into one follow-up run. */
  sync(): Promise<void>;
  /** Push one batch, then pull. */
  push(): Promise<void>;
  pull(): Promise<void>;
  getStatus(): SyncStatus;
  subscribe(listener: (status: SyncStatus) => void): () => void;
}

const MAX_BACKOFF_MS = 5 * 60_000;
const DEAD_LETTER_AFTER_ATTEMPTS = 5;
const DEAD_LETTER_CODES = new Set(["validation", "not_found"]);

function backoffMs(attempts: number): number {
  return Math.min(1000 * 2 ** attempts, MAX_BACKOFF_MS);
}

function repoOf(store: LocalStore, kind: EntityKind): EntityRepository<AnyEntity> {
  switch (kind) {
    case "task":
      return store.tasks as unknown as EntityRepository<AnyEntity>;
    case "note":
      return store.notes as unknown as EntityRepository<AnyEntity>;
    case "folder":
      return store.folders as unknown as EntityRepository<AnyEntity>;
    case "area":
      return store.areas as unknown as EntityRepository<AnyEntity>;
    case "project":
      return store.projects as unknown as EntityRepository<AnyEntity>;
    case "reminder":
      return store.reminders as unknown as EntityRepository<AnyEntity>;
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function errorCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "code" in err) {
    const code = (err as { code: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

export function createSyncEngine(options: SyncEngineOptions): SyncEngine {
  const {
    store,
    transport,
    network,
    intervalMs = 20_000,
    batchSize = 100,
    pullLimit = 500,
    kickDelayMs = 1_000,
  } = options;
  const timers = options.timers ?? defaultTimers;
  const clock = options.clock ?? (() => nowContext());
  const inFlight = options.services?.inFlight ?? new Set<string>();
  const nowMs = () => Date.parse(clock().now);

  let status: SyncStatus = { phase: "idle", pendingCount: 0, cursor: 0 };
  /** Mutations the last batch deferred for ordering reasons; a sync round repeats for them. */
  let lastDeferred = 0;
  const listeners = new Set<(s: SyncStatus) => void>();
  /** Earliest time (ms) each failed mutation may be retried. In memory only: a restart retries at once. */
  const retryAfter = new Map<string, number>();

  function setStatus(patch: Partial<SyncStatus>): void {
    let changed = false;
    for (const [k, v] of Object.entries(patch)) {
      if (status[k as keyof SyncStatus] !== v) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    status = { ...status, ...patch };
    for (const l of [...listeners]) l(status);
  }

  async function refreshFromStore(): Promise<void> {
    const [state, pendingCount] = await Promise.all([store.sync.get(), store.mutations.count()]);
    const patch: Partial<SyncStatus> = { cursor: state.cursor, pendingCount };
    if (state.lastPushAt !== undefined) patch.lastPushAt = state.lastPushAt;
    if (state.lastPullAt !== undefined) patch.lastPullAt = state.lastPullAt;
    if (state.lastError !== undefined) patch.lastError = state.lastError;
    if (state.lastErrorAt !== undefined) patch.lastErrorAt = state.lastErrorAt;
    setStatus(patch);
  }

  async function refreshCount(): Promise<void> {
    setStatus({ pendingCount: await store.mutations.count() });
  }

  async function recordError(err: unknown): Promise<void> {
    const message = errorMessage(err);
    const at = clock().now;
    await store.sync.update({ lastError: message, lastErrorAt: at });
    const patch: Partial<SyncStatus> = { phase: "error", lastError: message, lastErrorAt: at };
    if (errorCode(err) === "PROTOCOL_VERSION_UNSUPPORTED") patch.updateRequired = true;
    setStatus(patch);
  }

  /**
   * Oldest-first, keeping per-entity order. A revision-guarded write whose entity already has
   * an earlier mutation in this batch is held for the next push: its `baseRevision` can only
   * be rebased once the earlier mutation's echo has been pulled. Everything after a held
   * mutation on the same entity is held too, so order is preserved.
   */
  function selectBatch(pending: readonly PendingMutation[]): {
    batch: PendingMutation[];
    /** Mutations held only because of batch ordering; they become sendable after the pull. */
    deferred: number;
  } {
    const now = nowMs();
    const held = new Set<string>();
    const inBatch = new Set<string>();
    const batch: PendingMutation[] = [];
    let deferred = 0;
    for (const m of pending) {
      const target = mutationTargetId(m.mutation);
      const until = retryAfter.get(m.mutationId);
      const backoff = until !== undefined && until > now;
      if (held.has(target) || backoff || (inBatch.has(target) && isRevisionGuarded(m.mutation))) {
        if (!backoff) deferred += 1;
        held.add(target);
        continue;
      }
      batch.push(m);
      inBatch.add(target);
    }
    return { batch, deferred };
  }

  async function handleConflict(
    tx: LocalStore,
    c: PushConflict,
    sent: PendingMutation,
  ): Promise<void> {
    const record: ConflictRecord = {
      mutationId: c.mutationId,
      serverRevision: c.serverRevision,
      at: clock().now,
      mutation: sent.mutation,
    };
    if (c.conflictId !== undefined) record.conflictId = c.conflictId;
    await tx.kv.set(`conflict:${c.entity}:${c.id}`, record);
  }

  /** Push one batch. Returns how many mutations were sent (0 when nothing is sendable). */
  async function pushBatch(): Promise<number> {
    const { batch, deferred, deviceId } = await store.transaction(async (tx) => {
      const selected = selectBatch(await tx.mutations.pending(batchSize));
      await tx.mutations.markAttempted(selected.batch.map((m) => m.mutationId));
      return { ...selected, deviceId: (await tx.sync.get()).deviceId };
    });
    lastDeferred = deferred;
    if (batch.length === 0) return 0;
    for (const m of batch) inFlight.add(m.mutationId);
    const byId = new Map(batch.map((m) => [m.mutationId, m]));
    try {
      const response = await transport.push({
        protocolVersion: SYNC_PROTOCOL_VERSION,
        deviceId,
        mutations: batch.map(({ mutationId, createdAt, mutation }) => ({
          mutationId,
          deviceId,
          createdAt,
          mutation,
        })),
      });
      await store.transaction(async (tx) => {
        for (const c of response.conflicts) {
          const sent = byId.get(c.mutationId);
          if (sent) await handleConflict(tx, c, sent);
        }
        await tx.mutations.acknowledge([
          ...response.acknowledged,
          ...response.conflicts.map((c) => c.mutationId),
        ]);
        for (const r of response.rejected) {
          const sent = byId.get(r.mutationId);
          if (!sent) continue;
          const attempts = sent.attempts + 1;
          const message = `${r.code}: ${r.message}`;
          if (DEAD_LETTER_CODES.has(r.code) && attempts > DEAD_LETTER_AFTER_ATTEMPTS) {
            const record: DeadLetterRecord = {
              envelope: {
                mutationId: sent.mutationId,
                deviceId: sent.deviceId,
                createdAt: sent.createdAt,
                mutation: sent.mutation,
              },
              code: r.code,
              message: r.message,
              attempts,
              at: clock().now,
            };
            await tx.kv.set(`dead-letter:${r.mutationId}`, record);
            await tx.mutations.acknowledge([r.mutationId]);
            retryAfter.delete(r.mutationId);
          } else {
            await tx.mutations.recordAttempt(r.mutationId, message);
            retryAfter.set(r.mutationId, nowMs() + backoffMs(attempts));
          }
        }
        await tx.sync.update({ lastPushAt: clock().now });
      });
      for (const id of [...response.acknowledged, ...response.conflicts.map((c) => c.mutationId)]) {
        retryAfter.delete(id);
      }
      setStatus({ lastPushAt: clock().now });
      return batch.length;
    } catch (err) {
      const message = errorMessage(err);
      await store.transaction(async (tx) => {
        for (const m of batch) {
          await tx.mutations.recordAttempt(m.mutationId, message);
          retryAfter.set(m.mutationId, nowMs() + backoffMs(m.attempts + 1));
        }
      });
      throw err;
    } finally {
      for (const m of batch) inFlight.delete(m.mutationId);
    }
  }

  async function applyChange(tx: LocalStore, change: Change, myDeviceId: string): Promise<void> {
    await recordOwnRevision(tx, change, myDeviceId);
    const conflicts = (await tx.kv.get<ContentConflict[]>("sync:conflicts")) ?? [];
    const resolved = new Set(change.resolvedConflictIds ?? []);
    const incoming = change.conflicts ?? [];
    const nextConflicts = [
      ...conflicts.filter((c) => !resolved.has(c.id) && !incoming.some((n) => n.id === c.id)),
      ...incoming,
    ];
    if (incoming.length || resolved.size) {
      await tx.kv.set("sync:conflicts", nextConflicts);
      for (const c of conflicts.filter((item) => resolved.has(item.id))) {
        const remaining = nextConflicts.find((n) => n.kind === c.kind && n.entityId === c.entityId);
        if (!remaining) await tx.kv.delete(`conflict:${c.kind}:${c.entityId}`);
        else
          await tx.kv.set(`conflict:${c.kind}:${c.entityId}`, {
            mutationId: remaining.mutationId,
            serverRevision: remaining.serverRevision,
            conflictId: remaining.id,
            at: remaining.createdAt,
            mutation: remaining.mutation,
          } satisfies ConflictRecord);
      }
      for (const c of incoming)
        await tx.kv.set(`conflict:${c.kind}:${c.entityId}`, {
          mutationId: c.mutationId,
          serverRevision: c.serverRevision,
          conflictId: c.id,
          at: c.createdAt,
          mutation: c.mutation,
        } satisfies ConflictRecord);
    }
    const repo = repoOf(tx, change.entity);
    const dirty = await tx.mutations.pendingFor(change.id);
    if (dirty.length === 0) {
      await repo.put(change.row);
      return;
    }
    // Rebase: the server row is the base, local pending edits replay on top. The server
    // revision is kept so the next content write carries the right base.
    let row: AnyEntity = change.row;
    for (const m of dirty) {
      try {
        row = applyMutation(row, m.mutation, { now: m.createdAt, timeZone: clock().timeZone });
      } catch (err) {
        if (err instanceof MutationTargetMissing) continue;
        throw err;
      }
    }
    await repo.put({ ...row, revision: change.row.revision });
    if (change.deviceId !== myDeviceId || change.conflicts?.length) return;
    // Own echo: an acknowledged write of mine advanced the revision, so pending content
    // writes based on the previous revision are not conflicts. A change from another device
    // leaves `baseRevision` alone so the server reports the conflict instead of overwriting.
    for (const m of dirty) {
      if (!isRevisionGuarded(m.mutation)) continue;
      if (m.attempted) continue;
      if (m.mutation.baseRevision !== change.row.revision - 1) continue;
      await tx.mutations.replaceMutation(m.mutationId, {
        ...m.mutation,
        baseRevision: change.row.revision,
      });
    }
  }

  async function pullAll(): Promise<void> {
    const { deviceId, cursor: startCursor } = await store.sync.get();
    let cursor = startCursor;
    let hasMore = true;
    while (hasMore) {
      const page = await transport.pull({
        protocolVersion: SYNC_PROTOCOL_VERSION,
        deviceId,
        after: cursor,
        limit: pullLimit,
      });
      const at = clock().now;
      await store.transaction(async (tx) => {
        for (const change of page.changes) await applyChange(tx, change, deviceId);
        await tx.sync.update({ cursor: page.cursor, lastPullAt: at });
      });
      cursor = page.cursor;
      hasMore = page.hasMore;
      setStatus({ cursor, lastPullAt: at });
    }
  }

  async function guardedPull(): Promise<void> {
    if (options.enabled?.() === false) return;
    setStatus({ phase: "pulling" });
    try {
      await pullAll();
      setStatus({ phase: "idle" });
    } catch (err) {
      await recordError(err);
      throw err;
    } finally {
      await refreshCount();
    }
  }

  /** One batch. A pull must follow before the next batch so own echoes rebase pending content writes. */
  async function guardedPush(): Promise<number> {
    if (options.enabled?.() === false) return 0;
    setStatus({ phase: "pushing" });
    try {
      const sent = await pushBatch();
      setStatus({ phase: "idle" });
      return sent;
    } catch (err) {
      await recordError(err);
      throw err;
    } finally {
      await refreshCount();
    }
  }

  function isOffline(): boolean {
    return network !== undefined && !network.online;
  }

  async function runSync(): Promise<void> {
    if (status.updateRequired || options.enabled?.() === false) return;
    if (isOffline()) {
      setStatus({ phase: "offline" });
      await refreshCount();
      return;
    }
    // Push a batch, pull, and repeat while batches come back full or a batch deferred a
    // mutation behind an earlier one on the same entity (the pull in between rebases it).
    // A mutation enqueued during a push waits for the next round for the same reason.
    for (let round = 0; round < 50; round++) {
      let sent = 0;
      let failed = false;
      try {
        sent = await guardedPush();
      } catch {
        failed = true;
      }
      try {
        await guardedPull();
      } catch {
        failed = true;
      }
      if (failed) return;
      if (sent < batchSize && (lastDeferred === 0 || sent === 0)) break;
    }
    setStatus({ phase: "idle" });
  }

  let current: Promise<void> | undefined;
  let next: Promise<void> | undefined;

  function sync(): Promise<void> {
    if (!current) {
      current = runSync().finally(() => {
        current = undefined;
      });
      return current;
    }
    if (!next) {
      next = current
        .catch(() => undefined)
        .then(() => {
          next = undefined;
          return sync();
        });
    }
    return next;
  }

  let started = false;
  let intervalHandle: unknown;
  let kickHandle: unknown;
  let unsubNetwork: (() => void) | undefined;
  let unsubStore: (() => void) | undefined;

  function scheduleInterval(): void {
    intervalHandle = timers.setTimeout(() => {
      if (!started) return;
      if (!isOffline()) void sync().catch(() => undefined);
      scheduleInterval();
    }, intervalMs);
  }

  function start(): void {
    if (started) return;
    started = true;
    unsubStore = store.subscribe(["mutations"], () => void refreshCount());
    unsubNetwork = network?.subscribe((online) => {
      if (online) void sync().catch(() => undefined);
      else setStatus({ phase: "offline" });
    });
    void refreshFromStore().then(() => sync().catch(() => undefined));
    scheduleInterval();
  }

  function stop(): void {
    started = false;
    if (intervalHandle !== undefined) timers.clearTimeout(intervalHandle);
    if (kickHandle !== undefined) timers.clearTimeout(kickHandle);
    intervalHandle = undefined;
    kickHandle = undefined;
    unsubNetwork?.();
    unsubStore?.();
    unsubNetwork = undefined;
    unsubStore = undefined;
  }

  function kick(): void {
    if (kickHandle !== undefined) timers.clearTimeout(kickHandle);
    kickHandle = timers.setTimeout(() => {
      kickHandle = undefined;
      void sync().catch(() => undefined);
    }, kickDelayMs);
  }

  return {
    start,
    stop,
    kick,
    sync,
    push: async () => {
      if (options.enabled?.() === false) return;
      if (isOffline()) {
        setStatus({ phase: "offline" });
        return;
      }
      await guardedPush();
      await guardedPull();
    },
    pull: async () => {
      if (options.enabled?.() === false) return;
      if (isOffline()) {
        setStatus({ phase: "offline" });
        return;
      }
      await guardedPull();
    },
    getStatus: () => status,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
